/**
 * Helper functions for Deals page
 */

import type { DealProduct, GroupedDealProduct, DealsStats, MysteryType, DealsSummary, RumioBalance } from '../types';
import { TIER_UNLOCK_COSTS } from './constants';

/**
 * Format price with currency symbol
 */
export function formatPrice(price: number | null, currency = 'USD'): string {
  if (price === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(price);
}

/**
 * Get relative time string (e.g., "2 hours ago", "Just now")
 */
export function getRelativeTime(dateString: string | null): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Check if product is new (less than 24 hours old)
 */
export function isNew(firstSeen: string | null): boolean {
  if (!firstSeen) return false;
  const ageHours = (Date.now() - new Date(firstSeen).getTime()) / (1000 * 60 * 60);
  return ageHours < 24;
}

/**
 * Check if product is hot (big discount)
 */
export function isHot(discountPercent: number | null): boolean {
  return (discountPercent || 0) >= 30;
}

/**
 * Get product age in hours
 */
export function getProductAgeHours(firstSeen: string | null): number {
  if (!firstSeen) return Infinity;
  return (Date.now() - new Date(firstSeen).getTime()) / (1000 * 60 * 60);
}

/**
 * Get mystery type based on product age
 */
export function getMysteryType(firstSeen: string | null): MysteryType | null {
  const ageHours = getProductAgeHours(firstSeen);

  if (ageHours < 3) return 'super';
  if (ageHours < 24) return 'fresh';
  if (ageHours < 48) return 'standard';

  return null; // 48hr+ - fully visible
}

/**
 * Group products by name (for As-Is display)
 */
export function groupProductsByName(products: DealProduct[]): GroupedDealProduct[] {
  const groups = new Map<string, DealProduct[]>();

  for (const product of products) {
    const key = product.name || product.product_code;
    const existing = groups.get(key);
    if (existing) {
      existing.push(product);
    } else {
      groups.set(key, [product]);
    }
  }

  return Array.from(groups.entries()).map(([name, variants]) => {
    // Sort variants by price (lowest first)
    const sortedVariants = [...variants].sort((a, b) => (a.price || 0) - (b.price || 0));
    const prices = variants.map((v) => v.price || 0).filter((p) => p > 0);

    return {
      name,
      primary: sortedVariants[0],
      variants: sortedVariants,
      totalCount: variants.length,
      priceRange: {
        min: prices.length > 0 ? Math.min(...prices) : 0,
        max: prices.length > 0 ? Math.max(...prices) : 0,
      },
    };
  });
}

/**
 * Calculate stats for hero section
 */
export function calculateStats(
  products: DealProduct[],
  summary: DealsSummary | undefined
): DealsStats {
  const mysteryBoxes = products.filter((p) => p.is_mystery).length;
  const prizeHighlights = products.filter((p) => p.is_prize).length;

  return {
    totalDeals: (summary?.last_chance.count || 0) +
      (summary?.family_offers.count || 0) +
      (summary?.limited_time.count || 0) +
      (summary?.as_is.count || 0),
    mysteryBoxes,
    prizeHighlights,
    lastChance: summary?.last_chance.count || 0,
    familyOffers: summary?.family_offers.count || 0,
    limitedTime: summary?.limited_time.count || 0,
    asIs: summary?.as_is.count || 0,
  };
}

/**
 * Check if user can unlock a mystery box based on unified Rumio balance and tier cost
 */
export function canUnlock(
  mysteryType: MysteryType,
  balance: RumioBalance
): boolean {
  const cost = balance.tier_costs?.[mysteryType] ?? TIER_UNLOCK_COSTS[mysteryType];
  return balance.balance >= cost;
}

/**
 * Get the cost to unlock a mystery tier
 */
export function getTierUnlockCost(mysteryType: MysteryType): number {
  return TIER_UNLOCK_COSTS[mysteryType];
}

/**
 * Pluralize a word based on count
 */
export function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

/**
 * Format number with thousand separators
 */
export function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Get discount badge text
 */
export function getDiscountBadge(discountPercent: number | null): string | null {
  if (!discountPercent || discountPercent < 5) return null;
  return `-${discountPercent}%`;
}

/**
 * Check if a product should be highlighted as a prize
 */
export function isPrizeProduct(product: DealProduct): boolean {
  return product.is_prize === true && !product.is_mystery;
}

/**
 * Check if a product is a mystery box
 */
export function isMysteryBox(product: DealProduct): boolean {
  return product.is_mystery === true;
}

/**
 * Get the display name for a mystery type
 */
export function getMysteryTypeName(type: MysteryType): string {
  switch (type) {
    case 'super':
      return 'Super Fresh';
    case 'fresh':
      return 'Fresh';
    case 'standard':
      return 'Standard';
  }
}

/**
 * Sort products with mystery boxes at the end
 */
export function sortProductsWithMysteryLast(products: DealProduct[]): DealProduct[] {
  return [...products].sort((a, b) => {
    // Non-mystery products come first
    if (a.is_mystery && !b.is_mystery) return 1;
    if (!a.is_mystery && b.is_mystery) return -1;
    // Prize products come first among visible products
    if (a.is_prize && !b.is_prize) return -1;
    if (!a.is_prize && b.is_prize) return 1;
    return 0;
  });
}
